import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Table, Badge, Modal, Dropdown } from 'react-bootstrap';
import axios from 'axios';

const ManagerTables = () => {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');

  // Table management state
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [tablesError, setTablesError] = useState('');
  const [showTableModal, setShowTableModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableForm, setTableForm] = useState({
    tableNumber: '',
    menuId: '',
    assignedManagerIds: [],
    description: '',
    capacity: 4,
    location: '',
    isActive: true
  });
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [updatingQRCodes, setUpdatingQRCodes] = useState(false);

  // Menus for table assignment
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [menusError, setMenusError] = useState('');

  // Pagination for managers
  const [currentPage, setCurrentPage] = useState(1);
  const [managersPerPage] = useState(5);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?.canManageMenu) {
      fetchTables();
      fetchManagers();
    }
  }, [profile?.canManageMenu]);

  useEffect(() => {
    if (profile?.canManageMenu && profile?.restaurantId) {
      fetchMenus();
    }
  }, [profile?.canManageMenu, profile?.restaurantId]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('managerToken');
      if (!token) {
        setProfileError('Токен не найден');
        setLoadingProfile(false);
        return;
      }

      const res = await axios.get('/api/manager/profile', {
        headers: { 'x-auth-token': token }
      });
      setProfile(res.data);
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Ошибка загрузки профиля');
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchTables = async () => {
    try {
      setLoadingTables(true);
      setTablesError('');
      const token = localStorage.getItem('managerToken');
      const res = await axios.get('/api/tables', {
        headers: { 'x-auth-token': token }
      });
      setTables(res.data);
    } catch (err) {
      setTablesError(err.response?.data?.message || 'Ошибка загрузки столов');
    } finally {
      setLoadingTables(false);
    }
  };

  const fetchManagers = async () => {
    try {
      setLoadingManagers(true);
      const token = localStorage.getItem('managerToken');
      const res = await axios.get('/api/tables/managers', {
        headers: { 'x-auth-token': token }
      });
      setManagers(res.data);
    } catch (err) {
      console.error('Error fetching managers:', err);
    } finally {
      setLoadingManagers(false);
    }
  };

  const fetchMenus = async () => {
    try {
      setLoadingMenus(true);
      setMenusError('');
      const token = localStorage.getItem('managerToken');
      
      if (!profile?.restaurantId) {
        setMenusError('ID ресторана не найден');
        return;
      }
      
      const res = await axios.get(`/api/menu/${profile.restaurantId}/list`, {
        headers: { 'x-auth-token': token }
      });
      setMenus(res.data);
    } catch (err) {
      console.error('Error fetching menus:', err);
      setMenusError(err.response?.data?.message || 'Ошибка загрузки меню');
    } finally {
      setLoadingMenus(false);
    }
  };

  const handleCreateTable = () => {
    setEditingTable(null);
    setTableForm({
      tableNumber: '',
      menuId: '',
      assignedManagerIds: [],
      description: '',
      capacity: 4,
      location: '',
      isActive: true
    });
    setCurrentPage(1); // Reset pagination
    setShowTableModal(true);
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableForm({
      tableNumber: table.tableNumber,
      menuId: table.menuId?._id || '',
      assignedManagerIds: table.assignedManagerIds?.map(m => m._id) || [],
      description: table.description || '',
      capacity: table.capacity || 4,
      location: table.location || '',
      isActive: table.isActive === true
    });
    setCurrentPage(1); // Reset pagination
    setShowTableModal(true);
  };

  const handleSaveTable = async () => {
    try {
      const token = localStorage.getItem('managerToken');
      
      if (editingTable) {
        await axios.put(`/api/tables/${editingTable._id}`, tableForm, {
          headers: { 'x-auth-token': token }
        });
      } else {
        await axios.post('/api/tables', tableForm, {
          headers: { 'x-auth-token': token }
        });
      }
      
      setShowTableModal(false);
      fetchTables();
    } catch (err) {
      setTablesError(err.response?.data?.message || 'Ошибка сохранения стола');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот стол?')) return;
    
    try {
      const token = localStorage.getItem('managerToken');
      await axios.delete(`/api/tables/${tableId}`, {
        headers: { 'x-auth-token': token }
      });
      fetchTables();
    } catch (err) {
      setTablesError(err.response?.data?.message || 'Не удалось удалить стол');
    }
  };

  const handleUpdateAllQRCodes = async () => {
    if (!window.confirm('Обновить QR-коды для всех столов? Это может занять некоторое время.')) return;
    
    try {
      setUpdatingQRCodes(true);
      setTablesError('');
      const token = localStorage.getItem('managerToken');
      
      // Обновляем каждый стол, чтобы перегенерировать QR-коды
      for (const table of tables) {
        await axios.put(`/api/tables/${table._id}`, {
          tableNumber: table.tableNumber,
          menuId: table.menuId._id,
          assignedManagerIds: table.assignedManagerIds?.map(m => m._id) || [],
          description: table.description,
          capacity: table.capacity,
          location: table.location,
          isActive: table.isActive
        }, {
          headers: { 'x-auth-token': token }
        });
      }
      
      // Перезагружаем список столов
      await fetchTables();
      alert('QR-коды всех столов успешно обновлены!');
    } catch (err) {
      setTablesError(err.response?.data?.message || 'Ошибка обновления QR-кодов');
    } finally {
      setUpdatingQRCodes(false);
    }
  };

  const handleCloseTableModal = () => {
    setShowTableModal(false);
    setEditingTable(null);
  };

  // Download QR code for individual table
  const downloadTableQR = (table) => {
    if (!table.qrCodeDataUrl) {
      setTablesError('QR-код для этого стола не найден');
      return;
    }

    try {
      const link = document.createElement('a');
      link.download = `qr-table-${table.tableNumber}-${profile?.restaurantName || 'restaurant'}.png`;
      link.href = table.qrCodeDataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Download error:', e);
      setTablesError('Ошибка при скачивании QR-кода');
    }
  };

  // Download all tables QR codes as ZIP
  const downloadAllTablesQR = async () => {
    if (!tables.length) return;

    try {
      const JSZip = await import('jszip');
      const zip = new JSZip.default();
      
      // Добавляем каждый QR-код в ZIP
      tables.forEach((table, index) => {
        if (table.qrCodeDataUrl) {
          // Извлекаем данные из data URL
          const base64Data = table.qrCodeDataUrl.split(',')[1];
          zip.file(`qr-table-${table.tableNumber}.png`, base64Data, { base64: true });
        }
      });

      // Генерируем и скачиваем ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `qr-codes-${profile?.restaurantName || 'restaurant'}.zip`;
      link.href = URL.createObjectURL(content);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('ZIP download error:', e);
      setTablesError('Ошибка при создании архива с QR-кодами');
    }
  };

  // Print QR code for individual table
  const printTableQR = (table) => {
    if (!table.qrCodeDataUrl) {
      setTablesError('QR-код для этого стола не найден');
      return;
    }

    try {
      const w = window.open('', '_blank');
      if (!w) return;
      
      const restaurantName = profile?.restaurantName || 'Ресторан';
      const logoUrl = table.menuId?.branding?.logoUrl || '';
      const menuUrl = table.menuId 
        ? (profile?.subdomain 
            ? `https://${profile.subdomain}.menu.qr-code.by?menuId=${table.menuId._id}` 
            : `${window.location.origin}/menu/${profile?.restaurantId}?menuId=${table.menuId._id}`)
        : '';
      
      const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>QR-код стола ${table.tableNumber} - ${restaurantName}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        background: #fff;
        color: #333;
      }
      
      .qr-page {
        width: 100%;
        height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 40px 20px;
      }
      
      .logo {
        max-height: 80px;
        max-width: 200px;
        object-fit: contain;
        margin-bottom: 20px;
      }
      
      .restaurant-name {
        font-size: 24px;
        font-weight: bold;
        color: #333;
        margin-bottom: 10px;
      }
      
      .table-number {
        font-size: 32px;
        font-weight: bold;
        color: #007bff;
        margin-bottom: 20px;
      }
      
      .qr-code {
        width: 300px;
        height: 300px;
        margin: 20px 0;
        border: 2px solid #ddd;
        border-radius: 8px;
        padding: 10px;
        background: #fff;
      }
      
      .table-info {
        font-size: 14px;
        color: #555;
        margin-top: 20px;
        max-width: 400px;
        line-height: 1.5;
      }
      
      .instructions {
        font-size: 14px;
        color: #555;
        margin-top: 20px;
        max-width: 400px;
        line-height: 1.5;
      }
      
      .menu-link {
        font-size: 12px;
        color: #007bff;
        margin-top: 15px;
        word-break: break-all;
        padding: 8px;
        background: #f8f9fa;
        border-radius: 4px;
        border: 1px solid #e9ecef;
      }
      
      @media print {
        .qr-page {
          height: 100vh;
          page-break-after: avoid;
        }
        body {
          background: #fff;
          margin: 0;
          padding: 0;
        }
        .qr-code { border: 2px solid #000; }
        @page {
          margin: 0;
          padding: 0;
          size: A4;
        }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          height: 100vh !important;
          overflow: hidden;
        }
      }
    </style>
  </head>
  <body>
    <div class="qr-page">
      ${logoUrl ? `<img src="${logoUrl}" alt="Логотип" class="logo" />` : ''}
      <div class="restaurant-name">${restaurantName}</div>
      <div class="table-number">Стол № ${table.tableNumber}</div>
      <img src="${table.qrCodeDataUrl}" alt="QR код для стола ${table.tableNumber}" class="qr-code" />
      <div class="table-info">
        ${table.description ? `<p><strong>Описание:</strong> ${table.description}</p>` : ''}
        ${table.capacity ? `<p><strong>Вместимость:</strong> ${table.capacity} человек</p>` : ''}
        ${table.location ? `<p><strong>Местоположение:</strong> ${table.location}</p>` : ''}
        ${table.menuId ? `<p><strong>Меню:</strong> ${table.menuId.title}</p>` : ''}
      </div>
      <div class="instructions">
        Отсканируйте QR-код для просмотра меню и оформления заказа
      </div>
      ${menuUrl ? `
        <div class="menu-link">
          <strong>Ссылка на меню:</strong><br>
          ${menuUrl}&table=${table.tableNumber}
        </div>
      ` : ''}
    </div>
    <script>
      function safePrint() {
        try {
          document.body.style.margin = '0';
          document.body.style.padding = '0';
          document.documentElement.style.margin = '0';
          document.documentElement.style.padding = '0';
          window.focus();
          window.print();
        } catch(e) {
          console.error('Print error:', e);
        }
        setTimeout(() => {
          try { window.close(); } catch(e) {}
        }, 2000);
      }
      
      const images = document.querySelectorAll('img');
      let loadedImages = 0;
      
      function checkAllLoaded() {
        loadedImages++;
        if (loadedImages === images.length) {
          setTimeout(safePrint, 500);
        }
      }
      
      images.forEach(img => {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.onload = checkAllLoaded;
          img.onerror = checkAllLoaded;
        }
      });
      
      if (images.length === 0) {
        setTimeout(safePrint, 500);
      }
    </script>
  </body>
</html>`;
      
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      console.error('Print error:', e);
      setTablesError('Ошибка при печати QR-кода');
    }
  };

  // Print all tables QR codes
  const printAllTables = () => {
    if (!tables.length) return;
    
    try {
      const w = window.open('', '_blank');
      if (!w) return;
      
      const restaurantName = profile?.restaurantName || 'Ресторан';
      const logoUrl = tables[0]?.menuId?.branding?.logoUrl || '';
      
      const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>QR-коды столов - ${restaurantName}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        background: #fff;
        color: #333;
      }
      
      .qr-page {
        width: 100%;
        height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        page-break-after: always;
        padding: 40px 20px;
      }
      
      .qr-page:last-child {
        page-break-after: avoid;
      }
      
      .logo {
        max-height: 80px;
        max-width: 200px;
        object-fit: contain;
        margin-bottom: 20px;
      }
      
      .restaurant-name {
        font-size: 24px;
        font-weight: bold;
        color: #333;
        margin-bottom: 10px;
      }
      
      .table-number {
        font-size: 32px;
        font-weight: bold;
        color: #007bff;
        margin-bottom: 20px;
      }
      
      .qr-code {
        width: 300px;
        height: 300px;
        margin: 20px 0;
        border: 2px solid #ddd;
        border-radius: 8px;
        padding: 10px;
        background: #fff;
      }
      
      .table-info {
        font-size: 14px;
        color: #555;
        margin-top: 20px;
        max-width: 400px;
        line-height: 1.5;
      }
      
      .instructions {
        font-size: 14px;
        color: #555;
        margin-top: 20px;
        max-width: 400px;
        line-height: 1.5;
      }
      
      .menu-link {
        font-size: 12px;
        color: #007bff;
        margin-top: 15px;
        word-break: break-all;
        padding: 8px;
        background: #f8f9fa;
        border-radius: 4px;
        border: 1px solid #e9ecef;
      }
      
      @media print {
        .qr-page {
          height: 100vh;
          page-break-after: always;
        }
        .qr-page:last-child {
          page-break-after: avoid;
        }
        body {
          background: #fff;
          margin: 0;
          padding: 0;
        }
        .qr-code { border: 2px solid #000; }
        @page {
          margin: 0;
          padding: 0;
          size: A4;
        }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          height: 100vh !important;
          overflow: hidden;
        }
      }
    </style>
  </head>
  <body>
    ${tables.map((table) => `
      <div class="qr-page">
        ${logoUrl ? `<img src="${logoUrl}" alt="Логотип" class="logo" />` : ''}
        <div class="restaurant-name">${restaurantName}</div>
        <div class="table-number">${table.tableNumber ? `Стол № ${table.tableNumber}` : 'Доставка'}</div>
        <img src="${table.qrCodeDataUrl}" alt="QR код ${table.tableNumber ? `для стола ${table.tableNumber}` : 'для доставки'}" class="qr-code" />
        <div class="table-info">
          ${table.description ? `<p><strong>Описание:</strong> ${table.description}</p>` : ''}
          ${table.capacity ? `<p><strong>Вместимость:</strong> ${table.capacity} человек</p>` : ''}
          ${table.location ? `<p><strong>Местоположение:</strong> ${table.location}</p>` : ''}
          ${table.menuId ? `<p><strong>Меню:</strong> ${table.menuId.title}</p>` : ''}
        </div>
        <div class="instructions">
          Отсканируйте QR-код для просмотра меню и оформления заказа
        </div>
        ${table.menuId ? `
          <div class="menu-link">
            <strong>Ссылка на меню:</strong><br>
            ${profile?.subdomain 
              ? `https://${profile.subdomain}.menu.qr-code.by?menuId=${table.menuId._id}&table=${table.tableNumber}`
              : `${window.location.origin}/menu/${profile?.restaurantId}?menuId=${table.menuId._id}&table=${table.tableNumber}`
            }
          </div>
        ` : ''}
      </div>
    `).join('')}
    <script>
      function safePrint() {
        try {
          document.body.style.margin = '0';
          document.body.style.padding = '0';
          document.documentElement.style.margin = '0';
          document.documentElement.style.padding = '0';
          window.focus();
          window.print();
        } catch(e) {
          console.error('Print error:', e);
        }
        setTimeout(() => {
          try { window.close(); } catch(e) {}
        }, 2000);
      }
      
      const images = document.querySelectorAll('img');
      let loadedImages = 0;
      
      function checkAllLoaded() {
        loadedImages++;
        if (loadedImages === images.length) {
          setTimeout(safePrint, 500);
        }
      }
      
      images.forEach(img => {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.onload = checkAllLoaded;
          img.onerror = checkAllLoaded;
        }
      });
      
      if (images.length === 0) {
        setTimeout(safePrint, 500);
      }
    </script>
  </body>
</html>`;
      
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      console.error('Print error:', e);
      setTablesError('Ошибка при печати QR-кодов');
    }
  };

  if (loadingProfile) {
    return <div className="mt-5 text-center">Загрузка профиля менеджера...</div>;
  }

  if (profileError) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Ошибка загрузки профиля</Alert.Heading>
          <p>{profileError}</p>
        </Alert>
      </Container>
    );
  }

  if (!profile?.canManageMenu) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          <Alert.Heading>Недостаточно прав</Alert.Heading>
          <p>У вас нет прав для управления столами. Обратитесь к владельцу ресторана.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="mb-4">
        <Col>
          <h1>Управление столами</h1>
          <p className="mb-0">
            Ресторан: <strong>{profile?.restaurantName || '—'}</strong>
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                <span>Столы ресторана</span>
                <div className="d-flex gap-2">
                  <Dropdown>
                    <Dropdown.Toggle variant="success" size="sm" id="dropdown-basic">
                      QR-коды
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => downloadAllTablesQR()} disabled={tables.length === 0}>
                        📥 Скачать все QR-коды (ZIP)
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => printAllTables()} disabled={tables.length === 0}>
                        🖨️ Печать всех QR-кодов
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button variant="primary" size="sm" onClick={handleCreateTable}>
                    Добавить стол
                  </Button>
                </div>
              </Card.Title>

              {tablesError && <Alert variant="danger">{tablesError}</Alert>}

              {loadingTables ? (
                <div className="text-center py-3">
                  <div className="text-muted">Загрузка столов...</div>
                </div>
              ) : tables.length === 0 ? (
                <div className="text-center py-3">
                  <h6 className="text-muted">Столы не найдены</h6>
                </div>
              ) : (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Меню</th>
                      <th>Официант</th>
                      <th>Вместимость</th>
                      <th>Местоположение</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tables.map((table) => (
                      <tr key={table._id}>
                        <td>
                          <strong>{table.tableNumber}</strong>
                          {table.description && (
                            <div className="text-muted small">{table.description}</div>
                          )}
                        </td>
                        <td>
                          {table.menuId ? (
                            <div>
                              <div>{table.menuId.title}</div>
                              <Badge bg={table.menuId.mode === 'dine_in' ? 'primary' : 'success'}>
                                {table.menuId.mode === 'dine_in' ? 'В зале' : 'Доставка'}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {table.assignedManagerIds && table.assignedManagerIds.length > 0 ? (
                            <div>
                              {table.assignedManagerIds.map((manager, index) => (
                                <div key={manager._id || index}>
                                  <div className="small">{manager.name}</div>
                                  <div className="text-muted" style={{fontSize: '0.75rem'}}>{manager.phone}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted small">Не назначен</span>
                          )}
                        </td>
                        <td>{table.capacity} чел.</td>
                        <td>{table.location || '—'}</td>
                        <td>
                          <Badge bg={table.isActive ? 'success' : 'secondary'}>
                            {table.isActive ? 'Активен' : 'Отключен'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditTable(table)}
                              title="Редактировать"
                            >
                              ✏️
                            </Button>
                            <Dropdown>
                              <Dropdown.Toggle variant="outline-success" size="sm" id={`dropdown-${table._id}`}>
                                QR
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => downloadTableQR(table)}>
                                  📥 Скачать
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => printTableQR(table)}>
                                  🖨️ Печать
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteTable(table._id)}
                              title="Удалить"
                            >
                              🗑️
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Модальное окно для создания/редактирования стола */}
      <Modal show={showTableModal} onHide={handleCloseTableModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTable ? 'Редактировать стол' : 'Создать стол'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Номер стола *</Form.Label>
                  <Form.Control
                    type="text"
                    value={tableForm.tableNumber}
                    onChange={(e) => setTableForm(prev => ({ ...prev, tableNumber: e.target.value }))}
                    placeholder="Например: 1, A1, VIP-1"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Вместимость *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="20"
                    value={tableForm.capacity}
                    onChange={(e) => setTableForm(prev => ({ ...prev, capacity: parseInt(e.target.value) || 4 }))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Меню *</Form.Label>
                  <Form.Select
                    value={tableForm.menuId}
                    onChange={(e) => setTableForm(prev => ({ ...prev, menuId: e.target.value }))}
                    required
                    disabled={loadingMenus}
                  >
                    <option value="">
                      {loadingMenus ? 'Загрузка меню...' : 'Выберите меню'}
                    </option>
                    {menus.filter(menu => menu.mode === 'dine_in').map((menu) => (
                      <option key={menu._id} value={menu._id}>
                        {menu.title} — В зале
                      </option>
                    ))}
                  </Form.Select>
                  {menusError && (
                    <Form.Text className="text-danger">{menusError}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Назначенные менеджеры</Form.Label>
                  <div className="border rounded p-2" style={{minHeight: '100px', maxHeight: '200px', overflowY: 'auto'}}>
                    {tableForm.assignedManagerIds.length > 0 ? (
                      tableForm.assignedManagerIds.map((managerId) => {
                        const manager = managers.find(m => m._id === managerId);
                        return manager ? (
                          <div key={managerId} className="d-flex align-items-center justify-content-between mb-1 p-1 bg-light rounded">
                            <div>
                              <div className="small">{manager.name}</div>
                              <div className="text-muted" style={{fontSize: '0.7rem'}}>{manager.phone}</div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setTableForm(prev => ({
                                  ...prev,
                                  assignedManagerIds: prev.assignedManagerIds.filter(id => id !== managerId)
                                }));
                              }}
                              style={{padding: '2px 6px', fontSize: '0.7rem'}}
                              title="Удалить менеджера"
                            >
                              ×
                            </Button>
                          </div>
                        ) : null;
                      })
                    ) : (
                      <div className="text-muted small text-center py-2">Официанты не назначены</div>
                    )}
                  </div>
                  
                  <div className="mt-2">
                    <Form.Label className="small">Добавить официанта:</Form.Label>
                    
                    {/* Pagination for available managers */}
                    {(() => {
                      const availableManagers = managers.filter(manager => !tableForm.assignedManagerIds.includes(manager._id));
                      const totalPages = Math.ceil(availableManagers.length / managersPerPage);
                      const startIndex = (currentPage - 1) * managersPerPage;
                      const endIndex = startIndex + managersPerPage;
                      const currentManagers = availableManagers.slice(startIndex, endIndex);
                      
                      return (
                        <>
                          <div className="d-grid gap-1">
                            {currentManagers.map((manager) => (
                              <Button
                                key={manager._id}
                                variant="outline-success"
                                size="sm"
                                onClick={() => {
                                  setTableForm(prev => ({
                                    ...prev,
                                    assignedManagerIds: [...prev.assignedManagerIds, manager._id]
                                  }));
                                }}
                                style={{padding: '4px 8px', fontSize: '0.8rem'}}
                              >
                                + {manager.name && !manager.name.startsWith('Официант ') 
                                  ? `${manager.name} (${manager.phone})` 
                                  : manager.phone}
                              </Button>
                            ))}
                          </div>
                          
                          {/* Pagination controls */}
                          {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <div className="small text-muted">
                                Показано {startIndex + 1}-{Math.min(endIndex, availableManagers.length)} из {availableManagers.length} официантов
                              </div>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentPage === 1}
                                  style={{padding: '2px 6px', fontSize: '0.7rem'}}
                                >
                                  ←
                                </Button>
                                <span className="small text-muted px-2">
                                  {currentPage} / {totalPages}
                                </span>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={currentPage === totalPages}
                                  style={{padding: '2px 6px', fontSize: '0.7rem'}}
                                >
                                  →
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {availableManagers.length === 0 && (
                            <div className="text-muted small text-center py-1">Все официанты назначены</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Местоположение</Form.Label>
                  <Form.Control
                    type="text"
                    value={tableForm.location}
                    onChange={(e) => setTableForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Например: Зал, Терраса, VIP"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Статус</Form.Label>
                  <Form.Check
                    type="switch"
                    id="isActive"
                    label="Активен"
                    checked={tableForm.isActive}
                    onChange={(e) => setTableForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={tableForm.description}
                onChange={(e) => setTableForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Дополнительная информация о столе"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTableModal}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSaveTable}>
            {editingTable ? 'Сохранить изменения' : 'Создать стол'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ManagerTables;